import { useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import {
  CalendarBlank,
  CaretDown,
  CaretUp,
  CheckCircle,
  Plus,
  Repeat,
  Warning,
  X,
} from 'phosphor-react';

import {
  CollectionActivityReference,
  PaymentPlanOccurrenceUnit,
  formatMoney,
} from '@dill/dill-shared';

import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import {
  getBuyerPaymentPlans,
  createPaymentPlan,
  recordPaymentPlanPayment,
  cancelPaymentPlan,
} from '../../../../../redux/services/paymentPlanService';
import { getGroupedSuppliersInvoices } from '../../../../../redux/services/invoicesService';
import { DillButton, DillField, DillInput, DillSelect } from '../../../../general/Dill';
import { COLORS } from '../../../../../utils/colors';
import { openDillToast } from '../../../../../utils/helpers';
import {
  WEEKDAYS,
  formatPaymentPlanOccurrence,
  formatPaymentPlanConfirmationTimestamp,
} from '../../../../../utils/paymentPlanHelpers';
import ActivityReferenceSelector from '../../../suppliers/CollectionActivityTimeline/ActivityReferenceSelector/ActivityReferenceSelector';
import { PaymentPlanHistoryModal } from '../../../../general/PaymentPlanHistoryModal/PaymentPlanHistoryModal';
import { PaymentPlanHistoryList } from '../../../../general/PaymentPlanHistoryModal/PaymentPlanHistoryList';
import { StatementBuyerUser } from '../../../../general/AppSendStatementModal/AppSendStatementModal';

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const byLatestFirst = <T extends { createdAt?: Date | string | null }>(a: T, b: T) =>
  (b.createdAt ? new Date(b.createdAt).getTime() : 0) -
  (a.createdAt ? new Date(a.createdAt).getTime() : 0);

// Money fields in this form are kept as US-formatted strings (e.g. "1,234.56") end to end -
// formatAmountInput re-formats on every keystroke, parseAmountInput is the only place that
// converts back to a number, right where a number is actually needed (validation, math, payload).
const formatAmountInput = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDotIndex = cleaned.indexOf('.');
  const withOneDot =
    firstDotIndex === -1
      ? cleaned
      : cleaned.slice(0, firstDotIndex + 1) + cleaned.slice(firstDotIndex + 1).replace(/\./g, '');
  const [wholePart, decimalPart] = withOneDot.split('.');
  const formattedWhole = wholePart ? Number(wholePart).toLocaleString('en-US') : '';
  if (decimalPart === undefined) return formattedWhole;
  return `${formattedWhole}.${decimalPart.slice(0, 2)}`;
};

const parseAmountInput = (formatted: string): number =>
  parseFloat(formatted.replace(/,/g, '')) || 0;

// Which invoices get paid is one choice, not several independently-set fields - the UI only ever
// offers one active strategy at a time.
type InvoiceSelectionMode = 'SPECIFIC' | 'PAST_DUE' | 'ALL_OPEN' | 'FIXED_AMOUNT';

const invoiceSelectionModeOptions: { label: string; value: InvoiceSelectionMode }[] = [
  { label: 'Select specific invoices', value: 'SPECIFIC' },
  { label: 'Pay past due invoices', value: 'PAST_DUE' },
  { label: 'Pay full balance (all open invoices)', value: 'ALL_OPEN' },
  { label: 'Pay a fixed amount', value: 'FIXED_AMOUNT' },
];

// Only meaningful for "Pay a fixed amount", where the plan draws from every open invoice up to
// the cycle amount and needs to know which end of the list to start from.
type PaymentOrder = 'OLDEST_FIRST' | 'NEWEST_FIRST';

const paymentOrderOptions: { label: string; value: PaymentOrder }[] = [
  { label: 'Pay oldest invoices first', value: 'OLDEST_FIRST' },
  { label: 'Pay newest invoices first', value: 'NEWEST_FIRST' },
];

const occurrenceUnitOptions: { label: string; value: PaymentPlanOccurrenceUnit }[] = [
  { label: 'Week(s)', value: 'WEEK' },
  { label: 'Month(s)', value: 'MONTH' },
];

const StatTile = ({
  label,
  value,
  accent,
  onClick,
}: {
  label: string;
  value: string;
  accent?: boolean;
  onClick?: () => void;
}) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`rounded-xl border p-3 text-left w-full ${
        accent ? 'border-blue-100 bg-blue-50/60' : 'border-gray-100 bg-gray-50'
      } ${onClick ? 'hover:border-blue-300 transition-colors cursor-pointer' : ''}`}>
      <div className="text-2xs font-medium text-gray-500 mb-1">{label}</div>
      <div className={`text-lg font-semibold ${accent ? 'text-blue-700' : 'text-gray-900'}`}>
        {value}
      </div>
    </Tag>
  );
};

export const SupplierCustomerPaymentPlan = ({
  buyerId,
  buyerUsers,
}: {
  buyerId: string;
  buyerUsers?: StatementBuyerUser[];
}) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { buyerPaymentPlans, loadingList } = useAppSelector((state) => state.paymentPlans);
  const { groupedSuppliersInvoices, loadingList: invoiceLoadingList } = useAppSelector(
    (state) => state.invoice
  );

  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [invoiceSelectionMode, setInvoiceSelectionMode] =
    useState<InvoiceSelectionMode>('PAST_DUE');
  const [paymentOrder, setPaymentOrder] = useState<PaymentOrder>('OLDEST_FIRST');
  const [attachedInvoices, setAttachedInvoices] = useState<CollectionActivityReference[]>([]);
  const [totalAmount, setTotalAmount] = useState('');
  const [amountPerPayment, setAmountPerPayment] = useState('50');
  const [occurrenceUnit, setOccurrenceUnit] = useState<PaymentPlanOccurrenceUnit>('WEEK');
  const [occurrenceInterval, setOccurrenceInterval] = useState('1');
  const [dayOfWeek, setDayOfWeek] = useState(5); // Friday
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [manualPaymentAmounts, setManualPaymentAmounts] = useState<Record<string, string>>({});
  const [historyModalPlanId, setHistoryModalPlanId] = useState<string | null>(null);
  // Who gets the "confirm your plan" email - defaults to every app user on the buyer account,
  // minus any the supplier removes, plus any extra addresses they add.
  const [excludedRecipientUserIds, setExcludedRecipientUserIds] = useState<string[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  // Active plan cards start collapsed to a minimal summary row - expanding is per-card, and
  // nothing is expanded by default.
  const [expandedPlanIds, setExpandedPlanIds] = useState<Set<string>>(new Set());

  const isCreating = loadingList.includes('createPaymentPlan');
  const isRecordingPayment = loadingList.includes('recordPaymentPlanPayment');
  const isCancelling = loadingList.includes('cancelPaymentPlan');

  useEffect(() => {
    if (currentSupplier?.id && buyerId) {
      dispatch(getBuyerPaymentPlans({ supplierId: currentSupplier.id, buyerId }));
    }
  }, [currentSupplier?.id, buyerId, dispatch]);

  // Manual selection and the auto-pick strategies are mutually exclusive - leaving the picker
  // set from a previous mode would silently mix them, so clear it out on every mode switch.
  useEffect(() => {
    if (invoiceSelectionMode !== 'SPECIFIC') {
      setAttachedInvoices([]);
    }
  }, [invoiceSelectionMode]);

  // "Pay past due" / "Pay full balance" need the buyer's live invoice buckets to compute which
  // invoices to attach - fetch them the same way ActivityReferenceSelector does for manual mode.
  useEffect(() => {
    if (
      isCreateFormOpen &&
      (invoiceSelectionMode === 'PAST_DUE' || invoiceSelectionMode === 'ALL_OPEN') &&
      currentSupplier?.id
    ) {
      dispatch(
        getGroupedSuppliersInvoices({ supplierId: currentSupplier.id, buyerId, weeksLimit: 52 })
      );
    }
  }, [isCreateFormOpen, invoiceSelectionMode, currentSupplier?.id, buyerId, dispatch]);

  const isLoadingInvoiceBuckets = invoiceLoadingList.includes('getGroupedSuppliersInvoices');

  const buyerSupplierInvoices = useMemo(
    () => groupedSuppliersInvoices.find((s) => s.id === currentSupplier?.id)?.invoices,
    [groupedSuppliersInvoices, currentSupplier?.id]
  );

  const toReference = (inv: {
    id: string;
    docNumber: string;
    balance?: number | null;
    amount?: number | null;
  }): CollectionActivityReference => ({
    id: inv.id,
    type: 'INVOICE',
    description: inv.docNumber,
    amount: inv.balance ?? inv.amount ?? 0,
  });

  // Mirrors the backend's own "all open" bucketing (past due + current, not yet-due-today or paid).
  const pastDueReferences = useMemo(
    () => (buyerSupplierInvoices?.pastDueInvoices?.invoices || []).map(toReference),
    [buyerSupplierInvoices]
  );

  const allOpenReferences = useMemo(
    () =>
      [
        ...(buyerSupplierInvoices?.pastDueInvoices?.invoices || []),
        ...(buyerSupplierInvoices?.currentInvoices?.invoices || []),
      ].map(toReference),
    [buyerSupplierInvoices]
  );

  let autoReferences: CollectionActivityReference[] | null = null;
  if (invoiceSelectionMode === 'PAST_DUE') autoReferences = pastDueReferences;
  if (invoiceSelectionMode === 'ALL_OPEN') autoReferences = allOpenReferences;

  // Convenience only: suggest a total amount from whichever invoices are attached (manual pick or
  // an auto-selected bucket), still editable by hand for "Pay a fixed amount".
  useEffect(() => {
    const refs = invoiceSelectionMode === 'SPECIFIC' ? attachedInvoices : autoReferences || [];
    if (refs.length === 0) return;
    const refsTotal = refs.reduce((sum, ref) => sum + (ref.amount || 0), 0);
    if (refsTotal > 0) {
      setTotalAmount(formatAmountInput(refsTotal.toFixed(2)));
    }
  }, [invoiceSelectionMode, attachedInvoices, autoReferences]);

  // Whether the balance field is derived from a set of invoices rather than typed by hand.
  const isAutoBalance =
    (invoiceSelectionMode === 'SPECIFIC' && attachedInvoices.length > 0) ||
    (invoiceSelectionMode === 'PAST_DUE' && pastDueReferences.length > 0) ||
    (invoiceSelectionMode === 'ALL_OPEN' && allOpenReferences.length > 0);

  const describeAutoBucket = (refs: CollectionActivityReference[], noun: string) => {
    const count = refs.length;
    const total = refs.reduce((sum, ref) => sum + (ref.amount || 0), 0);
    const plural = count === 1 ? '' : 's';
    return `${count} ${noun} invoice${plural} totalling ${formatMoney(
      total,
      2
    )} will be attached to this plan.`;
  };

  const pastDueSummary =
    isLoadingInvoiceBuckets && !buyerSupplierInvoices
      ? 'Loading invoices...'
      : pastDueReferences.length > 0
      ? describeAutoBucket(pastDueReferences, 'past due')
      : 'No past due invoices found for this customer.';

  const allOpenSummary =
    isLoadingInvoiceBuckets && !buyerSupplierInvoices
      ? 'Loading invoices...'
      : allOpenReferences.length > 0
      ? describeAutoBucket(allOpenReferences, 'open')
      : 'No open invoices found for this customer.';

  const pendingPlans = useMemo(
    () =>
      buyerPaymentPlans
        .filter((plan) => plan.status === 'PENDING_CONFIRMATION')
        .sort(byLatestFirst),
    [buyerPaymentPlans]
  );

  const activePlans = useMemo(
    () => buyerPaymentPlans.filter((plan) => plan.status === 'ACTIVE').sort(byLatestFirst),
    [buyerPaymentPlans]
  );

  const pastPlans = useMemo(
    () =>
      buyerPaymentPlans
        .filter((plan) => plan.status === 'COMPLETED' || plan.status === 'CANCELLED')
        .sort(byLatestFirst),
    [buyerPaymentPlans]
  );

  const historyModalPlan = buyerPaymentPlans.find((plan) => plan.id === historyModalPlanId);

  const defaultRecipients = buyerUsers ?? [];
  const activeDefaultRecipients = defaultRecipients.filter(
    (u) => !excludedRecipientUserIds.includes(u.userId)
  );

  const handleAddEmail = () => {
    const trimmed = newEmailInput.trim();
    if (!isValidEmail(trimmed)) {
      setEmailInputError('Please enter a valid email address.');
      return;
    }
    const alreadyDefault = activeDefaultRecipients.some(
      (u) => u.email.toLowerCase() === trimmed.toLowerCase()
    );
    const alreadyAdded = additionalEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase());
    if (alreadyDefault || alreadyAdded) {
      setEmailInputError('This email is already in the recipient list.');
      return;
    }
    setAdditionalEmails((prev) => [...prev, trimmed]);
    setNewEmailInput('');
    setEmailInputError('');
  };

  const handleCreate = async () => {
    if (!currentSupplier?.id) return;
    const parsedTotal = parseAmountInput(totalAmount);
    const parsedPerPayment = parseAmountInput(amountPerPayment);
    const parsedInterval = parseInt(occurrenceInterval, 10);
    if (!parsedTotal || parsedTotal <= 0 || !parsedPerPayment || parsedPerPayment <= 0) {
      openDillToast({ message: 'Enter a valid balance and payment amount.', type: 'ERROR' });
      return;
    }
    if (!parsedInterval || parsedInterval < 1) {
      openDillToast({ message: 'Enter a valid repeat interval.', type: 'ERROR' });
      return;
    }
    if (invoiceSelectionMode === 'PAST_DUE' && pastDueReferences.length === 0) {
      openDillToast({ message: 'No past due invoices found for this customer.', type: 'ERROR' });
      return;
    }
    if (invoiceSelectionMode === 'ALL_OPEN' && allOpenReferences.length === 0) {
      openDillToast({ message: 'No open invoices found for this customer.', type: 'ERROR' });
      return;
    }

    // A pending, not-yet-added email in the input box still counts - mirrors AppSendStatementModal.
    const pendingEmail = newEmailInput.trim();
    if (pendingEmail && !isValidEmail(pendingEmail)) {
      setEmailInputError('Please enter a valid email address.');
      return;
    }
    const notificationEmails = [
      ...activeDefaultRecipients.map((u) => u.email),
      ...additionalEmails,
      ...(pendingEmail &&
      !activeDefaultRecipients.some((u) => u.email.toLowerCase() === pendingEmail.toLowerCase()) &&
      !additionalEmails.some((e) => e.toLowerCase() === pendingEmail.toLowerCase())
        ? [pendingEmail]
        : []),
    ];

    // Every mode but "Pay a fixed amount" attaches a curated invoice list; that one alone leaves
    // references empty and lets the plan auto-select from every open invoice in paymentOrder.
    let references: CollectionActivityReference[] | null = null;
    if (invoiceSelectionMode === 'SPECIFIC' && attachedInvoices.length > 0) {
      references = attachedInvoices;
    } else if (invoiceSelectionMode === 'PAST_DUE') {
      references = pastDueReferences;
    } else if (invoiceSelectionMode === 'ALL_OPEN') {
      references = allOpenReferences;
    }

    const result = await dispatch(
      createPaymentPlan({
        supplierId: currentSupplier.id,
        data: {
          supplierId: currentSupplier.id,
          buyerId,
          totalAmount: parsedTotal,
          amountPerPayment: parsedPerPayment,
          occurrenceUnit,
          occurrenceInterval: parsedInterval,
          dayOfWeek: occurrenceUnit === 'WEEK' ? dayOfWeek : null,
          dayOfMonth: occurrenceUnit === 'MONTH' ? parseInt(dayOfMonth, 10) || 1 : null,
          paymentOrder: invoiceSelectionMode === 'FIXED_AMOUNT' ? paymentOrder : 'OLDEST_FIRST',
          references,
          notificationEmails: notificationEmails.length > 0 ? notificationEmails : null,
          notes: null,
          createdBy: null,
          createdByRole: 'SUPPLIER',
        },
      })
    );

    if (result.type === 'paymentPlans/createPaymentPlan/fulfilled') {
      const payload = result.payload as any;
      if (payload?.success) {
        openDillToast({ message: 'Payment plan created.', type: 'SUCCESS' });
        setAttachedInvoices([]);
        setTotalAmount('');
        setExcludedRecipientUserIds([]);
        setAdditionalEmails([]);
        setNewEmailInput('');
        setEmailInputError('');
        setIsCreateFormOpen(false);
        return;
      }
    }
    openDillToast({
      message: (result.payload as string) || 'Failed to create payment plan.',
      type: 'ERROR',
    });
  };

  const handleRecordPayment = async (planId: string, remainingBalance: number) => {
    if (!currentSupplier?.id) return;
    const amount = parseAmountInput(manualPaymentAmounts[planId] || '');
    if (!amount || amount <= 0) {
      openDillToast({ message: 'Enter a valid payment amount.', type: 'ERROR' });
      return;
    }
    if (amount > remainingBalance) {
      openDillToast({ message: 'Amount cannot exceed the remaining balance.', type: 'ERROR' });
      return;
    }
    const result = await dispatch(
      recordPaymentPlanPayment({
        supplierId: currentSupplier.id,
        paymentPlanId: planId,
        amount,
      })
    );
    if (result.type === 'paymentPlans/recordPaymentPlanPayment/fulfilled') {
      const payload = result.payload as any;
      if (payload?.success) {
        openDillToast({ message: 'Payment recorded.', type: 'SUCCESS' });
        setManualPaymentAmounts((prev) => ({ ...prev, [planId]: '' }));
        return;
      }
    }
    openDillToast({
      message: (result.payload as string) || 'Failed to record payment.',
      type: 'ERROR',
    });
  };

  const handleCancelPlan = async (planId: string) => {
    if (!currentSupplier?.id) return;
    await dispatch(cancelPaymentPlan({ supplierId: currentSupplier.id, paymentPlanId: planId }));
    dispatch(getBuyerPaymentPlans({ supplierId: currentSupplier.id, buyerId }));
  };

  const toggleExpandedPlan = (planId: string) => {
    setExpandedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });
  };

  const renderPendingPlanCard = (plan: (typeof pendingPlans)[number]) => (
    <div key={plan.id} className="rounded-2xl border border-gray-200 shadow-sm bg-white p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1 text-2xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">
          <CalendarBlank size={12} weight="fill" />
          Awaiting customer confirmation
        </span>
      </div>

      <p className="text-2xs text-gray-500 mb-4">
        The customer has been emailed to confirm this plan and link a payment method. Once
        confirmed, {formatMoney(plan.amountPerPayment, 2)} will be charged automatically{' '}
        {formatPaymentPlanOccurrence(plan)} until the balance is paid off.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatTile label="Total balance" value={formatMoney(plan.totalAmount, 2)} accent />
        <StatTile label="Amount / cycle" value={formatMoney(plan.amountPerPayment, 2)} />
      </div>

      <div className="pt-4 border-t border-gray-100 flex justify-end">
        <DillButton
          label="Cancel Plan"
          variant="secondary"
          onClick={() => handleCancelPlan(plan.id)}
          isDisabled={isCancelling}
          height={38}
        />
      </div>
    </div>
  );

  const renderPlanCard = (plan: (typeof activePlans)[number]) => {
    const progressPct = Math.min(
      100,
      ((plan.totalAmount - plan.remainingBalance) / plan.totalAmount) * 100
    );
    const isExpanded = expandedPlanIds.has(plan.id);

    return (
      <div
        key={plan.id}
        className="rounded-2xl border border-gray-200 shadow-sm bg-white mb-5 overflow-hidden">
        <button
          type="button"
          onClick={() => toggleExpandedPlan(plan.id)}
          className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-gray-50 transition-colors">
          <div className="min-w-0">
            <div className="mb-1.5 flex items-baseline gap-1.5">
              <span className="text-xl font-bold text-gray-900">
                {formatMoney(plan.remainingBalance, 2)}
              </span>
              <span className="text-xs font-medium text-gray-400">remaining</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-2xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">
                <CheckCircle size={12} weight="fill" />
                Active
              </span>
              {plan.isOverdue && (
                <span className="inline-flex items-center gap-1 text-2xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                  <Warning size={12} weight="fill" />
                  Overdue
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-2xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                <Repeat size={11} />
                {formatMoney(plan.amountPerPayment, 2)}/cycle
              </span>
              <span className="inline-flex items-center gap-1 text-2xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                <CalendarBlank size={11} />
                Next due {moment(plan.nextDueDate).format('MMM DD, YYYY')}
              </span>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-2xs font-medium text-gray-600 border border-gray-200 rounded-full px-2.5 py-1 shrink-0">
            Details
            {isExpanded ? (
              <CaretUp size={12} color={COLORS.GREY_500} />
            ) : (
              <CaretDown size={12} color={COLORS.GREY_500} />
            )}
          </span>
        </button>

        <div className="px-5 pb-4 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-2xs font-medium text-gray-500 shrink-0">
            {Math.round(progressPct)}% paid off
          </span>
        </div>

        {isExpanded && (
          <div className="px-5 pb-5">
            {plan.linkedInvoiceIds && plan.linkedInvoiceIds.length > 0 && (
              <div className="mb-4">
                <span className="text-2xs font-medium text-gray-500 block mb-1">
                  Selected Invoices
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {(plan.references || []).map((ref, i) => (
                    <span
                      key={`${ref.id}-${i}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                      {ref.description || ref.id}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mb-4 pt-4 border-t border-gray-100">
              <StatTile
                label="Remaining balance"
                value={formatMoney(plan.remainingBalance, 2)}
                accent
              />
              <StatTile label="Amount / cycle" value={formatMoney(plan.amountPerPayment, 2)} />
              <StatTile label="Payments made" value={`${plan.paymentsMadeCount}`} />
            </div>

            <div className="pt-4 border-t border-gray-100 mb-4">
              <span className="text-2xs font-medium text-gray-500 block mb-2">
                Record a manual payment
              </span>
              <div className="flex items-end gap-2">
                <div className="w-32">
                  <DillInput
                    value={manualPaymentAmounts[plan.id] || ''}
                    onChange={(value) =>
                      setManualPaymentAmounts((prev) => ({
                        ...prev,
                        [plan.id]: formatAmountInput(value),
                      }))
                    }
                    type="text"
                    placeholder="e.g. 50"
                  />
                </div>
                <DillButton
                  label="Record Payment"
                  variant="secondary"
                  onClick={() => handleRecordPayment(plan.id, plan.remainingBalance)}
                  isDisabled={isRecordingPayment || !manualPaymentAmounts[plan.id]}
                  height={38}
                />
                <DillButton
                  label="Cancel Plan"
                  variant="secondary"
                  onClick={() => handleCancelPlan(plan.id)}
                  isDisabled={isCancelling}
                  height={38}
                />
              </div>
            </div>

            {plan.confirmedAt && (
              <div className="mb-4 pt-4 border-t border-gray-100">
                <span className="text-2xs font-medium text-gray-500 block mb-1">Authorization</span>
                <p className="text-2xs text-gray-400 leading-relaxed">
                  Confirmed {formatPaymentPlanConfirmationTimestamp(plan.confirmedAt)}
                  {plan.confirmedByEmail ? ` by ${plan.confirmedByEmail}` : ''}
                  {plan.confirmedByIp ? ` from IP ${plan.confirmedByIp}` : ''}.
                </p>
              </div>
            )}

            <div className="pt-4 border-t border-gray-100">
              <span className="text-2xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                Payment history
              </span>
              <PaymentPlanHistoryList
                paymentHistory={plan.paymentHistory}
                missedPayments={plan.missedPayments}
                failedCharges={plan.failedCharges}
                accountType="supplier"
                maxHeightClassName="max-h-60"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full pt-4 pb-8 overflow-y-auto">
      <div className="w-full">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50">
              <Repeat size={16} color={COLORS.PRIMARY_500} weight="bold" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Payment Plans</h3>
          </div>
          {!isCreateFormOpen && (
            <DillButton
              label="New Payment Plan"
              variant="secondary"
              icon={<Plus size={14} />}
              onClick={() => setIsCreateFormOpen(true)}
              height={36}
            />
          )}
        </div>
        <p className="text-xs text-gray-500 mb-5 max-w-lg leading-relaxed">
          Sets up a recurring payment schedule for this customer. The customer will be emailed to
          confirm the plan and link a payment method - once confirmed, each installment is charged
          automatically. You can also record a payment manually if they pay another way.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 min-w-0">
            {isCreateFormOpen && (
              <div className="rounded-2xl border border-gray-200 shadow-sm bg-white p-5 mb-5">
                <div className="mb-4">
                  <DillField label="Which invoices to pay">
                    <DillSelect
                      value={invoiceSelectionMode}
                      onChange={(v) => setInvoiceSelectionMode(v as InvoiceSelectionMode)}
                      options={invoiceSelectionModeOptions}
                    />
                  </DillField>
                </div>

                {invoiceSelectionMode === 'SPECIFIC' && (
                  <div className="mb-4">
                    <ActivityReferenceSelector
                      label="Invoices"
                      selectedLabel="Selected Invoices"
                      buyerId={buyerId}
                      value={attachedInvoices}
                      onChange={setAttachedInvoices}
                      allowedTypes={['INVOICE']}
                    />
                  </div>
                )}

                {invoiceSelectionMode === 'PAST_DUE' && (
                  <p className="text-xs text-gray-500 mb-4">{pastDueSummary}</p>
                )}

                {invoiceSelectionMode === 'ALL_OPEN' && (
                  <p className="text-xs text-gray-500 mb-4">{allOpenSummary}</p>
                )}

                {invoiceSelectionMode === 'FIXED_AMOUNT' && (
                  <div className="mb-4">
                    <DillField label="Invoice order">
                      <DillSelect
                        value={paymentOrder}
                        onChange={(v) => setPaymentOrder(v as PaymentOrder)}
                        options={paymentOrderOptions}
                      />
                    </DillField>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <DillField
                    label={isAutoBalance ? 'Balance owed (from invoices)' : 'Balance owed'}>
                    <DillInput
                      value={totalAmount}
                      onChange={(value) => setTotalAmount(formatAmountInput(value))}
                      type="text"
                      placeholder="e.g. 200"
                      disabled={isAutoBalance}
                    />
                  </DillField>
                  <DillField label="Amount per payment">
                    <DillInput
                      value={amountPerPayment}
                      onChange={(value) => setAmountPerPayment(formatAmountInput(value))}
                      type="text"
                      placeholder="e.g. 50"
                    />
                  </DillField>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
                  <DillField label="Repeats every">
                    <DillInput
                      value={occurrenceInterval}
                      onChange={setOccurrenceInterval}
                      type="number"
                      placeholder="e.g. 1"
                    />
                  </DillField>
                  <DillField label="Unit">
                    <DillSelect
                      value={occurrenceUnit}
                      onChange={(v) => setOccurrenceUnit(v as PaymentPlanOccurrenceUnit)}
                      options={occurrenceUnitOptions}
                    />
                  </DillField>
                  {occurrenceUnit === 'WEEK' ? (
                    <DillField label="On">
                      <DillSelect
                        value={String(dayOfWeek)}
                        onChange={(v) => setDayOfWeek(Number(v))}
                        options={WEEKDAYS.map((day, index) => ({
                          label: day,
                          value: String(index),
                        }))}
                      />
                    </DillField>
                  ) : (
                    <DillField label="On day">
                      <DillInput
                        value={dayOfMonth}
                        onChange={setDayOfMonth}
                        type="number"
                        placeholder="1-31"
                      />
                    </DillField>
                  )}
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 mb-5">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Notify by email
                    </p>
                    <p className="text-2xs text-gray-400 mt-0.5">
                      Who gets emailed to confirm the plan and link a payment method. Defaults to
                      the customer's users, but you can remove any or add more.
                    </p>
                  </div>

                  {(activeDefaultRecipients.length > 0 || additionalEmails.length > 0) && (
                    <div className="flex flex-wrap gap-1.5">
                      {activeDefaultRecipients.map((u) => (
                        <span
                          key={u.userId}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-700 shadow-sm">
                          {u.email}
                          <button
                            type="button"
                            onClick={() =>
                              setExcludedRecipientUserIds((prev) => [...prev, u.userId])
                            }
                            className="ml-0.5 text-gray-400 hover:text-gray-700 cursor-pointer bg-transparent border-none leading-none">
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                      {additionalEmails.map((email, i) => (
                        <span
                          key={`${email}-${i}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700">
                          {email}
                          <button
                            type="button"
                            onClick={() =>
                              setAdditionalEmails((prev) => prev.filter((e) => e !== email))
                            }
                            className="ml-0.5 text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none">
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <DillInput
                        value={newEmailInput}
                        onChange={(v) => {
                          setNewEmailInput(v);
                          if (emailInputError) setEmailInputError('');
                        }}
                        placeholder="Add email address"
                      />
                      {emailInputError && (
                        <p className="text-xs text-red-600 mt-1">{emailInputError}</p>
                      )}
                    </div>
                    <DillButton
                      label="Add"
                      variant="secondary"
                      onClick={handleAddEmail}
                      height={38}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <DillButton
                    label="Cancel"
                    variant="secondary"
                    onClick={() => setIsCreateFormOpen(false)}
                  />
                  <DillButton
                    label="Create Payment Plan"
                    onClick={handleCreate}
                    isDisabled={isCreating}
                  />
                </div>
              </div>
            )}

            {pendingPlans.map(renderPendingPlanCard)}
            {activePlans.map(renderPlanCard)}

            {!isCreateFormOpen && pendingPlans.length === 0 && activePlans.length === 0 && (
              <div className="rounded-2xl border border-gray-200 shadow-sm bg-white p-8 mb-5 text-center">
                <p className="text-xs text-gray-400">No active payment plans for this customer.</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-1 min-w-0">
            <div className="rounded-2xl border border-gray-200 shadow-sm bg-white p-4">
              <div className="font-semibold text-sm text-gray-900 mb-3">Plan history</div>
              {pastPlans.length === 0 ? (
                <p className="text-2xs text-gray-400 text-center py-4">No past plans yet</p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {pastPlans.map((plan) => (
                    <button
                      type="button"
                      key={plan.id}
                      onClick={() => setHistoryModalPlanId(plan.id)}
                      className="rounded-xl border border-gray-100 p-3 text-left w-full hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {formatMoney(plan.totalAmount, 2)}
                        </span>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            plan.status === 'COMPLETED'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                          {plan.status === 'COMPLETED' ? 'Completed' : 'Cancelled'}
                        </span>
                      </div>
                      <div className="text-2xs text-gray-500">
                        {plan.createdAt ? moment(plan.createdAt).format('MMM DD, YYYY') : ''} ·{' '}
                        {plan.paymentsMadeCount} payment{plan.paymentsMadeCount === 1 ? '' : 's'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PaymentPlanHistoryModal
        open={!!historyModalPlanId}
        onClose={() => setHistoryModalPlanId(null)}
        paymentHistory={historyModalPlan?.paymentHistory}
        missedPayments={historyModalPlan?.missedPayments}
        failedCharges={historyModalPlan?.failedCharges}
        accountType="supplier"
      />
    </div>
  );
};

export default SupplierCustomerPaymentPlan;
